import { useTranslation } from "react-i18next";
import Modal from "../../../../../../components/base/Modal";
import type { Notification } from "../../../../../../store/admin/notificationsStore";
import type { User } from "../../../../../../store/admin/usersStore";
import type { ThemeClasses } from "../../../../shared";
import { formatDate, formatScheduleDate } from "../../../../../../utils/getDate";
import { useDisableCRUD } from "../../../../../../store/admin/companiesStore";
import { CRUDTooltip } from "../../../../../../components/base/CRUDTooltip";
interface NotificationDetailsModalProps {
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  notification: Notification;
  users: User[];
  onClose: () => void;
  onEdit: () => void;
}

const getPriorityColor = (priority: string) => {
  switch (priority) {
    case "low":
      return "text-gray-500 bg-gray-500/10";
    case "medium":
      return "text-blue-500 bg-blue-500/10";
    case "high":
      return "text-orange-500 bg-orange-500/10";
    case "urgent":
      return "text-red-500 bg-red-500/10";
    default:
      return "text-gray-500 bg-gray-500/10";
  }
};

export default function NotificationDetailsModal({
  isDarkMode,
  themeClasses,
  notification,
  users,
  onClose,
  onEdit,
}: NotificationDetailsModalProps) {
  const { t } = useTranslation("adminSections");
  const disableCRUD = useDisableCRUD();

  // Get recipient names from addresses array
  const getRecipientNames = () => {
    if (notification.type_of_addresse === "all") {
      return t("notifications.form.allUsers");
    }
    if (notification.addresses && notification.addresses.length > 0) {
      const recipientNames = notification.addresses
        .map((userId) => {
          const user = users.find((u) => u.id === userId);
          return user ? `${user.first_name} ${user.last_name}` : userId;
        });
      return recipientNames;
    }
    // Fallback to legacy addressee field
    const addresseeUser = users.find((user) => user.id === notification.addressee);
    return addresseeUser
      ? [`${addresseeUser.first_name} ${addresseeUser.last_name}`]
      : [notification.addressee || "-"];
  };

  const recipientNames = getRecipientNames();

  return (
    <Modal
      themeClasses={themeClasses}
      onClose={onClose}
      title={t("notifications.details")}
      buttonAction={
        <CRUDTooltip>
          <button
            disabled={disableCRUD}
            onClick={onEdit}
            className="bg-primary-500 hover:bg-primary-400 text-white px-4 py-2 rounded-lg font-medium transition-colors"
          >
            {t("notifications.actions.edit")}
          </button>
        </CRUDTooltip>
      }
      content={
        <div className="space-y-4 w-[500px] max-w-full">
          <div
            className={`grid grid-cols-1 sm:grid-cols-3 gap-4 ${isDarkMode ? "bg-dark-card" : "bg-gray-100/50"
              } border ${themeClasses.cardBorder} rounded-lg p-4`}
          >
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>ID</p>
              <p className={`${themeClasses.text} font-mono text-sm truncate`}>
                {notification.id}
              </p>
            </div>
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>
                {t("notifications.fields.createdAt")}
              </p>
              <p className={`text-sm ${themeClasses.text}`}>
                {notification.createdAt?._seconds
                  ? formatDate(notification.createdAt._seconds)
                  : "-"}
              </p>
            </div>
            <div>
              <p className={`text-sm ${themeClasses.textSecondary} mb-1`}>
                {t("notifications.fields.updatedAt")}
              </p>
              <p className={`text-sm ${themeClasses.text}`}>
                {notification.updatedAt?._seconds
                  ? formatDate(notification.updatedAt._seconds)
                  : "-"}
              </p>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("notifications.fields.addresseeType")}
            </label>
            <div
              className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } p-2 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } text-sm`}
            >
              <span className="px-2 py-0.5 rounded-full text-sm">
                {notification.type_of_addresse === "HiLYcf6ndou53xpXNJ02" || notification.type_of_addresse === "specific" ? t("notifications.fields.specificUsers") : t("notifications.fields.allUsers")}
              </span>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("notifications.fields.addressee")}
            </label>
            <div
              className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } p-2 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } text-sm max-h-24 overflow-y-auto`}
            >
              {typeof recipientNames === "string" ? (
                <span className="text-primary-500 font-medium">{recipientNames}</span>
              ) : (
                <div className="flex flex-wrap gap-1">
                  {recipientNames.map((name, index) => (
                    <span
                      key={index}
                      className={`px-2 py-0.5 rounded-full text-xs ${isDarkMode ? "bg-dark-border" : "bg-gray-200"
                        }`}
                    >
                      {name}
                    </span>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("notifications.fields.title")}
            </label>
            <div
              className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } p-2 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } text-sm`}
            >
              {notification.title}
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <label className={`text-sm font-semibold ${themeClasses.text}`}>
              {t("notifications.fields.description")}
            </label>
            <div
              className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                } p-2 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                } text-sm min-h-[60px]`}
            >
              {notification.description}
            </div>
          </div>

          {notification.image && (
            <div className="flex flex-col gap-1">
              <label className={`text-sm font-semibold ${themeClasses.text}`}>
                {t("notifications.fields.imageSent")}
              </label>
              <div
                className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } p-2 border ${themeClasses.cardBorder} rounded-lg`}
              >
                <img
                  src={notification.image}
                  alt="Notification"
                  className="max-h-32 rounded-md object-cover"
                />
              </div>
            </div>
          )}

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div className="flex flex-col gap-1">
              <label className={`text-sm font-semibold ${themeClasses.text}`}>
                {t("notifications.fields.priority")}
              </label>
              <div
                className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } p-2 border ${themeClasses.cardBorder} rounded-lg`}
              >
                <span
                  className={`px-2 py-0.5 rounded-full text-xs ${getPriorityColor(
                    notification.priority
                  )}`}
                >
                  {t(`notifications.priority.${notification.priority}`)}
                </span>
              </div>
            </div>

            <div className="flex flex-col gap-1">
              <label className={`text-sm font-semibold ${themeClasses.text}`}>
                {t("notifications.fields.addresseeType")}
              </label>
              <div
                className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } p-2 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                  } text-sm`}
              >
                <span className="px-2 py-0.5 rounded-full text-sm">
                  {notification.type_of_addresse === "HiLYcf6ndou53xpXNJ02" || notification.type_of_addresse === "specific" ? t("notifications.fields.specificUsers") : t("notifications.fields.allUsers")}
                </span>
              </div>
            </div>
          </div>

          {notification.schedule_shipment && (
            <div className="flex flex-col gap-1">
              <label className={`text-sm font-semibold ${themeClasses.text}`}>
                {t("notifications.fields.scheduleShipment")}
              </label>
              <div
                className={`${isDarkMode ? "bg-dark-card" : "bg-gray-50"
                  } p-2 border ${themeClasses.cardBorder} rounded-lg ${themeClasses.text
                  } text-sm`}
              >
                {formatScheduleDate(notification.schedule_shipment)}
              </div>
            </div>
          )}

          {notification.url && (
            <div className="flex flex-col gap-1">
              <label className={`text-sm font-semibold ${themeClasses.text}`}>
                {t("notifications.fields.link", "Enlace")}
              </label>
              <a
                href={notification.url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 bg-primary-500 hover:bg-primary-400 text-white px-4 py-2 rounded-lg font-medium transition-colors w-fit"
              >
                {t("notifications.actions.viewDetails", "Ver detalles")}
                <i className="ri-external-link-line text-sm" />
              </a>
            </div>
          )}
        </div>
      }
    />
  );
}
